import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { blueMain as blueMainColor } from '../../../../styles/scss/_colors.scss';

import IconMapPin from '../../../../ui/components/IconMapPin';
import IconOperator from '../../../../ui/components/IconOperator';
import CategoryBadge from '../../../../ui/components/CategoryBadge';
import Link from '../component/Link';
import Container from '../component/Container';
import MasterRange from '../component/MasterRange';
import Skeleton from '../component/skeleton';
import QuickLinks from '../component/QuickLinks';
import styles from '../index.scss';
import Desktop from '../../../components/Responsive/Desktop';

const FIND_RESELLER_TYPE = 'FIND_RESELLER';
const SUPPORT_TYPE = 'SUPPORT';

const CategoryPageDescription = ({
    skeleton,
    skeletonDescription,
    skeletonSmallDescription,
    quickLinks,
    quickLinkSkeleton,
    descriptionData: {
        title,
        badge,
        description,
        longDescription,
        imageUrl,
        topPageLinks,
        range,
        categoryLevel,
    },
}) => {
    const getLinkData = linkType =>
        topPageLinks
            ? topPageLinks.reduce(
                  (linksObj, linkData) => (linkData.linkType === linkType ? linkData : linksObj),
                  {},
              )
            : null;
    const findResellerData = getLinkData(FIND_RESELLER_TYPE);
    const supportData = getLinkData(SUPPORT_TYPE);

    const categoryPageHeaderClasses = classNames({
        [styles.CategoryPageContainer]: true,
        [styles.CategoryPageHeader]: true,
    });
    const findResellerClasses = classNames({
        [styles.actionItem]: true,
        'find-reseller-analytics': true,
    });
    const supportClasses = classNames({
        [styles.actionItem]: true,
        'support-analytics': true,
    });

    return (
        <Container>
            <div className={categoryPageHeaderClasses}>
                <div className={styles.HeadingWrapperContainer}>
                    {skeleton && <Skeleton />}
                    <h1
                        style={skeleton ? { overflow: 'hidden' } : {}}
                        className={styles.HeadingContainer}
                    >
                        <span>{title}</span>
                    </h1>
                    {badge && <CategoryBadge badge={badge} />}
                </div>
                {topPageLinks && (
                    <div className={styles.actionsBar}>
                        <Link className={findResellerClasses} to={findResellerData.pageUrl}>
                            {skeleton && <Skeleton />}
                            <IconMapPin fill={blueMainColor} />
                            <span>{findResellerData.name}</span>
                        </Link>
                        <Link className={supportClasses} to={supportData.pageUrl}>
                            {skeleton && <Skeleton />}
                            <IconOperator fill={blueMainColor} />
                            <span>{supportData.name}</span>
                        </Link>
                    </div>
                )}
            </div>
            {(range || description || longDescription || imageUrl || skeleton) && (
                <div className={styles.CategoryPageContainer}>
                    <div
                        style={!skeletonSmallDescription ? { marginBottom: 0 } : {}}
                        className={styles.CategoryPageTextContent}
                    >
                        {range && !skeleton && <MasterRange range={range} />}
                        {description && !skeleton && (
                            <div
                                className={
                                    categoryLevel === 'familyCategories'
                                        ? styles.DescriptionTextBriefBold
                                        : styles.DescriptionTextBrief
                                }
                            >
                                <div dangerouslySetInnerHTML={{ __html: description }} />
                            </div>
                        )}
                        {skeleton && skeletonSmallDescription && !skeletonDescription && (
                            <div
                                style={{ width: '50%', position: 'relative' }}
                                className={styles.DescriptionTextBriefBold}
                            >
                                ...
                                <Skeleton />
                            </div>
                        )}
                        {skeleton && skeletonDescription ? (
                            <React.Fragment>
                                {new Array(5).fill(true).map((item, i) =>
                                    i < 4 ? (
                                        <Skeleton
                                            key={`skeleton-${i}`}
                                            styleForTemplate={{
                                                height: '12px',
                                                width: '90%',
                                                marginBottom: '5px',
                                            }}
                                            withTemplate={true}
                                        />
                                    ) : (
                                        <Skeleton
                                            key={`skeleton-${i}`}
                                            styleForTemplate={{
                                                height: '12px',
                                                width: '70%',
                                                marginBottom: '5px',
                                            }}
                                            withTemplate={true}
                                        />
                                    ),
                                )}
                            </React.Fragment>
                        ) : null}
                        {longDescription && !skeleton && (
                            <div
                                className={styles.DescriptionText}
                                dangerouslySetInnerHTML={{ __html: longDescription }}
                            />
                        )}
                        {quickLinkSkeleton && (
                            <React.Fragment>
                                <div className={styles.quickLinkSkeleton}>
                                    <Skeleton />
                                </div>
                                <div className={styles.quickLinkSkeleton}>
                                    <Skeleton />
                                </div>
                                <div className={styles.quickLinkSkeleton}>
                                    <Skeleton />
                                </div>
                                <Desktop>
                                    <div className={styles.quickLinkSkeleton}>
                                        <Skeleton />
                                    </div>
                                </Desktop>
                            </React.Fragment>
                        )}
                        {quickLinks && <QuickLinks data={quickLinks} />}
                    </div>
                    {imageUrl && !skeleton && (
                        <div className={styles.CategoryPageImage}>
                            <img src={imageUrl} loading="lazy" alt={title} />
                        </div>
                    )}
                    {skeleton && skeletonDescription && (
                        <div className={styles.CategoryPageImage}>
                            {skeleton && (
                                <Skeleton
                                    styleForTemplate={{ width: '100%', height: '100px' }}
                                    withTemplate={true}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </Container>
    );
};

CategoryPageDescription.propTypes = {
    skeleton: PropTypes.bool,
    skeletonDescription: PropTypes.bool,
    skeletonSmallDescription: PropTypes.bool,
    quickLinks: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    quickLinkSkeleton: PropTypes.bool.isRequired,
    descriptionData: PropTypes.shape({
        title: PropTypes.string.isRequired,
        badge: PropTypes.shape({
            type: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
        }),
        description: PropTypes.string,
        longDescription: PropTypes.string,
        imageUrl: PropTypes.string,
        topPageLinks: PropTypes.arrayOf(
            PropTypes.shape({
                pageUrl: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
                linkType: PropTypes.string.isRequired,
            }),
        ),
        range: PropTypes.object,
        categoryLevel: PropTypes.string,
    }).isRequired,
};

export default React.memo(CategoryPageDescription);
